import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { company, navLinks } from "../mock";
import { Button } from "./ui/button";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const goBook = () => {
    if (location.pathname === "/") {
      document.getElementById("book-form")?.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate("/#book-form");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-[0_4px_24px_rgba(0,0,0,0.06)]" : "bg-white"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0">
            <img src={company.logo} alt={company.name} className="h-16 w-auto object-contain" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {navLinks.map((l) => {
              const active = location.pathname === l.to;
              return (
                <Link
                  key={l.to}
                  to={l.to}
                  className={`relative text-[15px] font-medium tracking-wide transition-colors hover:text-gold ${
                    active ? "text-gold" : "text-ink"
                  }`}
                >
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-[2px] bg-gold transition-all duration-300 ${
                      active ? "w-full" : "w-0"
                    }`}
                  />
                </Link>
              );
            })}
          </nav>

          {/* Right actions */}
          <div className="hidden lg:flex items-center gap-4">
            <a href={company.phoneHref} className="flex items-center gap-2 group">
              <span className="grid place-items-center h-10 w-10 rounded-full bg-ink text-gold group-hover:bg-gold group-hover:text-ink transition-colors">
                <Phone size={17} />
              </span>
              <span className="text-sm font-semibold text-ink leading-tight max-w-[120px]">
                {company.phone}
              </span>
            </a>
            <Button
              onClick={goBook}
              className="bg-gold hover:bg-gold-dark text-ink font-semibold rounded-full px-6 h-11 shadow-[0_6px_18px_rgba(201,162,74,0.35)]"
            >
              Book Free Site Visit
            </Button>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden grid place-items-center h-11 w-11 rounded-full bg-ink text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-white border-t border-gold/20 shadow-lg">
          <nav className="flex flex-col px-6 py-4">
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="py-3 text-base font-medium text-ink border-b border-gold/10 hover:text-gold"
              >
                {l.label}
              </Link>
            ))}
            <a href={company.phoneHref} className="py-3 flex items-center gap-2 text-ink font-semibold">
              <Phone size={18} className="text-gold" /> {company.phone}
            </a>
            <Button
              onClick={goBook}
              className="mt-3 bg-gold hover:bg-gold-dark text-ink font-semibold rounded-full h-11"
            >
              Book Free Site Visit
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
}
